import { memo, useDeferredValue, useMemo } from 'react';
import { Scheduler } from './UI/Scheduler';
import { RootState } from 'packages/blu-booking/src/store';
import { useSelector } from 'react-redux';
import { useUserHoursCalendar } from 'packages/blu-booking/src/data_fetching/UserHours/UserHoursCalendar';
import { useUserHoursShift } from 'packages/blu-booking/src/data_fetching/UserHours/UserHoursShift';
import { Stack } from '@mui/material';
import { CalendarLoader } from '../../UI/CalendarLoader/CalendarLoader';
import { selectSoloOrario } from '../../hooks/useSoloOrario';
import { useFinestraOraria } from '../../hooks/useFinestraOraria';
import { selectAppuntamentiPersonalePronti } from './utility_objects/AppuntamentiPersonale';
import { getOrariPersonale } from './utility_objects/OrariPersonale';

/**
 * Wrapper del calendario personale.
 * Il data fetching è stato spostato nel hook useSchedulerData
 * usato direttamente da Scheduler.
 */
export const PersonalCalendarInner = memo(
  function CalendarPersonal(): JSX.Element {
    const { filtersLoad } = useSelector(
      (state: RootState) => state.bluBookingCalendarFilters
    );
    const { selectedDate, view, selectedUsers } = useSelector(
      (state: RootState) => state.bluBookingPersonalCalendar
    );
    // Riga Ore del riquadro Visualizzazione di questo calendario
    const soloOrario = useSelector(selectSoloOrario('personale'));
    const appointments = useSelector(
      (state: RootState) => state.bluBookingaAppointments.appointments
    );
    const appuntamentiPronti = useSelector(selectAppuntamentiPersonalePronti);

    const {
      data: calendar,
      isLoading: calendarIsLoading,
      isSuccess: calendarIsSuccess,
    } = useUserHoursCalendar({
      selectedDate,
      view,
      userId: selectedUsers[0]?.id,
      options: {
        enabled: filtersLoad ?? false,
        refetchInterval: 300000,
      },
    });
    const calendarPerUser = useDeferredValue(calendar);

    const { data: shift, isLoading: shiftIsLoading } = useUserHoursShift({
      selectedDate,
      view,
      userId: selectedUsers[0]?.id,
      options: {
        enabled: filtersLoad ?? false,
        refetchInterval: 300000,
      },
    });
    const shiftPerUser = useDeferredValue(shift);

    // Gli orari che arrivano allo Scheduler sono quelli della data e dei filtri
    // correnti: per un render dopo l'arrivo dei dati il valore differito è
    // ancora quello di prima (nessuno, dopo un caricamento), e lo Scheduler
    // appena rimontato lo riceve un render dopo
    const sfondoPronto = calendarIsSuccess && calendarPerUser === calendar;

    // La banda che il Personale disegna: le fasce di tutti i punti vendita
    // dell'operatore, dagli stessi orari differiti che riceve lo Scheduler
    const orari = useMemo(
      () => getOrariPersonale(calendarPerUser),
      [calendarPerUser]
    );

    // Qui e non nello Scheduler, che si rimonta a ogni data, vista o operatore:
    // la finestra tenuta passa da un montaggio all'altro, senza tornare alle
    // 5-24. Il Personale non ha il filtro del punto vendita: la riga Ore c'è
    // sempre
    const finestra = useFinestraOraria({
      soloOrario,
      conPulsante: true,
      view,
      selectedDate,
      orari,
      appuntamenti: appointments,
      appuntamentiPronti,
      sfondoPronto,
    });

    const GetContent = useMemo(() => {
      if (calendarIsLoading || shiftIsLoading)
        return (
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            flex="1"
            sx={{
              height: '100%',
              width: '100%',
              backgroundColor: 'var(--base-foreground-color) ',
            }}
          />
        );
      return (
        <Scheduler
          calendarPerUser={calendarPerUser}
          shiftPerUser={shiftPerUser}
          startHour={finestra.startHour}
          endHour={finestra.endHour}
          finestraInAttesa={finestra.inAttesa}
          finestraProvvisoria={finestra.provvisoria}
        />
      );
    }, [
      calendarIsLoading,
      calendarPerUser,
      shiftIsLoading,
      shiftPerUser,
      finestra.startHour,
      finestra.endHour,
      finestra.inAttesa,
      finestra.provvisoria,
    ]);

    return (
      <div key="Personal" style={{ height: '100%' }}>
        {GetContent}
        <CalendarLoader open={calendarIsLoading || shiftIsLoading} />
      </div>
    );
  }
);
